import { useMemo } from 'react'

import { RectangleMeshProps } from '@editor/interfaces/canvas/rectangleMesh'
import useProjectionPlane from '@editor/pages/projects/blueprint-editor/hooks/useProjectionPlane'
import { RootState } from '@editor/store/app'
import { useThree } from '@react-three/fiber'
import { assign } from 'lodash'
import { useSelector } from 'react-redux'
import { Matrix4, Quaternion, Vector3 } from 'three'

const useParams = ({ positions, transformation, label }: RectangleMeshProps) => {
  // Hooks
  const { camera, gl } = useThree()
  const { projectionDirections } = useProjectionPlane(camera, gl)

  // Store
  const labelFontSize = useSelector((state: RootState) => state.toolUtil.core.labelFontSize)

  const { position, quaternion, matrix, zAlignMatrix } = useMemo(() => {
    const pos = new Vector3()
    const quat = new Quaternion()
    const mtx = new Matrix4().fromArray(transformation)

    // Create rotation matrix to align plane normal with z-axis
    const zAxis = new Vector3(0, 0, 1)
    const zMtrx = new Matrix4()

    if (!positions) {
      mtx.decompose(pos, quat, new Vector3())
    }

    if (projectionDirections && !projectionDirections.normal.equals(zAxis)) {
      const axis = projectionDirections.normal.clone().cross(zAxis).normalize()
      const angle = Math.acos(projectionDirections.normal.dot(zAxis))
      zMtrx.makeRotationAxis(axis, angle)
    }

    return { position: pos, quaternion: quat, matrix: mtx, zAlignMatrix: zMtrx }
  }, [positions, transformation, projectionDirections])

  // Label Configs
  const labelConfig = useMemo(
    (): RectangleMeshProps['label'] =>
      label
        ? assign(
            {},
            {
              color: 'black',
              backgroundColor: 'yellow',
              fontSize: labelFontSize,
            },
            label,
          )
        : undefined,
    [label, labelFontSize],
  )

  return { position, quaternion, matrix, zAlignMatrix, labelConfig }
}

export default useParams
